import React, {useState} from 'react'
import {OrgLicensureData} from '../../redux/organizations'
import {
  ArrowDown,
  CETitle,
  LicensedBox,
  LicensureButton,
  LicensureButtonDisabled,
  PaginationText,
  PaperCard,
  PATitle,
  PCMTitle,
  PDDTitle,
  PJMTitle,
  PPDTitle,
  StyledSelect,
  TableError,
  TableHint,
  TableSuccess,
} from './styles'
import {Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material'
import CheckIcon from '../../images/icons/white-check-icon.svg'
import ArrowForward from '../../images/icons/arrow-forward.svg'
import ArrowBack from '../../images/icons/arrow-back.svg'
import ArrowForwardDisabled from '../../images/icons/arrow-forward-disabled.svg'
import ArrowBackDisabled from '../../images/icons/arrow-back-disabled.svg'
import ArrowDownIcon from '../../images/icons/arrow-down.svg'
import {useWindowDimensions} from '../../hooks/useWindowDimensions'
import pluralize from 'pluralize'

type Props = {
  data: OrgLicensureData
}

const LicensureTable = ({data}: Props) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const {isMobile} = useWindowDimensions()
  const items = data.items

  const totalPages = Math.ceil(items.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const currentData = items.slice(startIndex, startIndex + itemsPerPage)

  const handlePageChange = (page: number) => {
    if (page < 1) page = 1
    if (page > totalPages) page = totalPages
    setCurrentPage(page)
  }

  const firstDisabled = currentPage === 1
  const lastDisabled = currentPage === totalPages

  const FirstButtonComponent = firstDisabled
    ? LicensureButtonDisabled
    : LicensureButton
  const LastButtonComponent = lastDisabled
    ? LicensureButtonDisabled
    : LicensureButton

  return (
    <PaperCard
      marginTop={2}
      width={'100%'}
      boxSizing={'border-box'}
      overflow={isMobile ? 'auto' : ''}
    >
      <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
        <FirstButtonComponent
          disabled={firstDisabled}
          onClick={() => handlePageChange(1)}
        >
          <img src={firstDisabled ? ArrowBackDisabled : ArrowBack} /> First
        </FirstButtonComponent>
        <FirstButtonComponent
          disabled={firstDisabled}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          <span>
            <img src={firstDisabled ? ArrowBackDisabled : ArrowBack} />
            <img src={firstDisabled ? ArrowBackDisabled : ArrowBack} />
          </span>
          Previous
        </FirstButtonComponent>
        <PaginationText>
          Page {currentPage} of {totalPages}
        </PaginationText>
        <LastButtonComponent
          disabled={lastDisabled}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Next <img src={lastDisabled ? ArrowForwardDisabled : ArrowForward} />
        </LastButtonComponent>
        <LastButtonComponent
          disabled={lastDisabled}
          onClick={() => handlePageChange(totalPages)}
        >
          Last{' '}
          <span>
            <img src={lastDisabled ? ArrowForwardDisabled : ArrowForward} />
            <img src={lastDisabled ? ArrowForwardDisabled : ArrowForward} />
          </span>
        </LastButtonComponent>

        <div style={{position: 'relative'}}>
          <StyledSelect
            value={itemsPerPage}
            onChange={e => {
              setItemsPerPage(Number(e.target.value))
              setCurrentPage(1)
            }}
          >
            <option value={10}>Per Page: 10</option>
            <option value={20}>Per Page: 20</option>
            <option value={50}>Per Page: 50</option>
            <option value={100}>Per Page: 100</option>
          </StyledSelect>
          <ArrowDown>
            <img src={ArrowDownIcon} />
          </ArrowDown>
        </div>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width={'20%'}>User Name</TableCell>
            <TableCell width={'11%'}>
              <PCMTitle>PcM</PCMTitle>
            </TableCell>
            <TableCell width={'11%'}>
              <PJMTitle>PjM</PJMTitle>
            </TableCell>
            <TableCell width={'11%'}>
              <PATitle>PA</PATitle>
            </TableCell>
            <TableCell width={'11%'}>
              <PPDTitle>PPD</PPDTitle>
            </TableCell>
            <TableCell width={'11%'}>
              <PDDTitle>PDD</PDDTitle>
            </TableCell>
            <TableCell width={'11%'}>
              <CETitle>CE</CETitle>
            </TableCell>
            <TableCell width={'11%'}>Licensure</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currentData.map((row, key) => {
            const isFirst = key === 0
            const isLast = key + 1 === currentData.length

            const padding = isFirst
              ? '20px 20px 8px'
              : isLast
              ? '8px 20px 20px'
              : '8px 20px'

            return (
              <TableRow sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                <TableCell sx={{padding}}>{row.name}</TableCell>
                <TableCell sx={{padding}}>
                  {row.courses.pcm.scheduleds ? (
                    <>
                      <TableHint>
                        {pluralize(
                          'Scheduled',
                          row.courses.pcm.scheduleds,
                          true,
                        )}
                      </TableHint>
                      <br />
                      <TableHint>
                        {pluralize('Attempt', row.courses.pcm.attempts, true)}
                      </TableHint>
                      <br />
                      {row.courses.pcm.finalStatus === 'passed' ? (
                        <TableSuccess>Passed</TableSuccess>
                      ) : row.courses.pcm.finalStatus === 'failed' ? (
                        <TableError>Not Passed</TableError>
                      ) : (
                        <>-</>
                      )}
                    </>
                  ) : (
                    <>-</>
                  )}
                </TableCell>
                <TableCell sx={{padding}}>
                  {row.courses.pjm.scheduleds ? (
                    <>
                      <TableHint>
                        {pluralize(
                          'Scheduled',
                          row.courses.pjm.scheduleds,
                          true,
                        )}
                      </TableHint>
                      <br />
                      <TableHint>
                        {pluralize('Attempt', row.courses.pjm.attempts, true)}
                      </TableHint>
                      <br />
                      {row.courses.pjm.finalStatus === 'passed' ? (
                        <TableSuccess>Passed</TableSuccess>
                      ) : row.courses.pjm.finalStatus === 'failed' ? (
                        <TableError>Not Passed</TableError>
                      ) : (
                        <>-</>
                      )}
                    </>
                  ) : (
                    <>-</>
                  )}
                </TableCell>
                <TableCell sx={{padding}}>
                  {row.courses.pa.scheduleds ? (
                    <>
                      <TableHint>
                        {pluralize(
                          'Scheduled',
                          row.courses.pa.scheduleds,
                          true,
                        )}
                      </TableHint>
                      <br />
                      <TableHint>
                        {pluralize('Attempt', row.courses.pa.attempts, true)}
                      </TableHint>
                      <br />
                      {row.courses.pa.finalStatus === 'passed' ? (
                        <TableSuccess>Passed</TableSuccess>
                      ) : row.courses.pa.finalStatus === 'failed' ? (
                        <TableError>Not Passed</TableError>
                      ) : (
                        <>-</>
                      )}
                    </>
                  ) : (
                    <>-</>
                  )}
                </TableCell>
                <TableCell sx={{padding}}>
                  {row.courses.ppd.scheduleds ? (
                    <>
                      <TableHint>
                        {pluralize(
                          'Scheduled',
                          row.courses.ppd.scheduleds,
                          true,
                        )}
                      </TableHint>
                      <br />
                      <TableHint>
                        {pluralize('Attempt', row.courses.ppd.attempts, true)}
                      </TableHint>
                      <br />
                      {row.courses.ppd.finalStatus === 'passed' ? (
                        <TableSuccess>Passed</TableSuccess>
                      ) : row.courses.ppd.finalStatus === 'failed' ? (
                        <TableError>Not Passed</TableError>
                      ) : (
                        <>-</>
                      )}
                    </>
                  ) : (
                    <>-</>
                  )}
                </TableCell>
                <TableCell sx={{padding}}>
                  {row.courses.pdd.scheduleds ? (
                    <>
                      <TableHint>
                        {pluralize(
                          'Scheduled',
                          row.courses.pdd.scheduleds,
                          true,
                        )}
                      </TableHint>
                      <br />
                      <TableHint>
                        {pluralize('Attempt', row.courses.pdd.attempts, true)}
                      </TableHint>
                      <br />
                      {row.courses.pdd.finalStatus === 'passed' ? (
                        <TableSuccess>Passed</TableSuccess>
                      ) : row.courses.pdd.finalStatus === 'failed' ? (
                        <TableError>Not Passed</TableError>
                      ) : (
                        <>-</>
                      )}
                    </>
                  ) : (
                    <>-</>
                  )}
                </TableCell>
                <TableCell sx={{padding}}>
                  {row.courses.ce.scheduleds ? (
                    <>
                      <TableHint>
                        {pluralize(
                          'Scheduled',
                          row.courses.ce.scheduleds,
                          true,
                        )}
                      </TableHint>
                      <br />
                      <TableHint>
                        {pluralize('Attempt', row.courses.ce.attempts, true)}
                      </TableHint>
                      <br />
                      {row.courses.ce.finalStatus === 'passed' ? (
                        <TableSuccess>Passed</TableSuccess>
                      ) : row.courses.ce.finalStatus === 'failed' ? (
                        <TableError>Not Passed</TableError>
                      ) : (
                        <>-</>
                      )}
                    </>
                  ) : (
                    <>-</>
                  )}
                </TableCell>
                <TableCell sx={{padding}}>
                  {row.gotLicense && (
                    <LicensedBox>
                      <img src={CheckIcon} alt={'Licensed'} /> Licensed
                    </LicensedBox>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </PaperCard>
  )
}

export default LicensureTable
